'use client';
import React, { useState } from 'react';
import { Check, Pencil } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { UnifiedStoryTemplate } from '@/lib/unified-story-templates';
import { groupTemplatesForPicker } from '@/lib/story-templates/picker-groups';

interface StoryTemplatePickerProps {
  templates: UnifiedStoryTemplate[];
  occasionFirst: boolean;
  selectedId: string | null;
  titleFor: (t: UnifiedStoryTemplate) => string;
  onSelect: (t: UnifiedStoryTemplate) => void;
  onWriteOwn?: () => void;
}

export function StoryTemplatePicker({ templates, occasionFirst, selectedId, titleFor, onSelect, onWriteOwn }: StoryTemplatePickerProps) {
  const groups = groupTemplatesForPicker(templates, occasionFirst);
  const [showMore, setShowMore] = useState(!occasionFirst);

  return (
    <div className="space-y-4">
      {groups.map((g) => {
        const collapsed = occasionFirst && g.key === 'more' && !showMore;
        return (
          <section key={g.key} aria-label={g.label}>
            {occasionFirst && <h4 className="mb-2 text-sm font-semibold text-gray-800">{g.label}</h4>}
            {collapsed ? (
              <button type="button" className="text-sm font-medium text-purple-700 hover:underline" onClick={() => setShowMore(true)}>
                Show {g.templates.length} more adventure stories
              </button>
            ) : (
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                {g.templates.map((t) => {
                  const isSelected = selectedId === t.id;
                  return (
                    <Card
                      key={t.id}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isSelected}
                      className={cn(
                        'relative cursor-pointer p-3 transition-all hover:shadow-md',
                        isSelected ? 'border-2 border-purple-600 bg-purple-50' : 'border border-gray-200 hover:border-purple-300',
                      )}
                      onClick={() => onSelect(t)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(t); } }}
                    >
                      {isSelected && (
                        <div className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-purple-600">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}
                      <div className="space-y-1">
                        {/* Classic layout keeps today's category badge; no thumbnails (assets 404 on prod). */}
                        {!occasionFirst && (
                          <Badge variant="secondary" className="mb-1 text-xs">{t.category}</Badge>
                        )}
                        <h5 className={cn('line-clamp-2 text-sm font-medium', isSelected ? 'text-purple-900' : 'text-gray-800')}>
                          {titleFor(t)}
                        </h5>
                        <p className="line-clamp-2 text-xs text-gray-500">{t.description}</p>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
      {occasionFirst && onWriteOwn && (
        <button type="button" onClick={onWriteOwn} className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-700">
          <Pencil className="h-4 w-4" /> Or write your own story idea
        </button>
      )}
    </div>
  );
}
